import { Panel } from '@/components/admin/Field'
import { lumi } from '@/lib/lumi'
import type { HostMessage, MessageStatus } from '@/types/host'
import { Check, Clock, MapPin, Phone, Trash2, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'

type View = MessageStatus

const VIEWS: { id: View; label: string }[] = [
  { id: 'pending', label: 'Pendentes' },
  { id: 'approved', label: 'Aprovados' },
  { id: 'rejected', label: 'Rejeitados' },
]

function formatDate(value?: string) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
}

// Recados antigos (sem status) já eram entregues aos locutores: contam como aprovados.
function statusOf(item: HostMessage): MessageStatus {
  return item.status || 'approved'
}

function MessagesApproval() {
  const [items, setItems] = useState<HostMessage[]>([])
  const [loading, setLoading] = useState(true)
  const [view, setView] = useState<View>('pending')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.host_messages.list({ sort: { createdAt: -1 }, limit: 300 })
      setItems((list || []) as HostMessage[])
    } catch (error) {
      console.error('Falha ao carregar recados:', error)
      toast.error('Não foi possível carregar os recados.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const setStatus = async (id: string | undefined, status: MessageStatus) => {
    if (!id) return
    try {
      await lumi.entities.host_messages.update(id, { status })
      setItems((prev) => prev.map((item) => (item._id === id ? { ...item, status } : item)))
      toast.success(status === 'approved' ? 'Recado aprovado e enviado ao locutor.' : status === 'rejected' ? 'Recado rejeitado.' : 'Recado voltou para pendentes.')
    } catch (error) {
      console.error('Falha ao atualizar recado:', error)
      toast.error('Não foi possível atualizar o recado.')
    }
  }

  const remove = async (id?: string) => {
    if (!id || !window.confirm('Apagar este recado definitivamente?')) return
    try {
      await lumi.entities.host_messages.delete(id)
      setItems((prev) => prev.filter((item) => item._id !== id))
    } catch (error) {
      console.error('Falha ao apagar recado:', error)
      toast.error('Não foi possível apagar o recado.')
    }
  }

  const counts = useMemo(() => {
    const base: Record<MessageStatus, number> = { pending: 0, approved: 0, rejected: 0 }
    items.forEach((item) => {
      base[statusOf(item)] += 1
    })
    return base
  }, [items])

  const current = items.filter((item) => statusOf(item) === view)

  return (
    <Panel title="Aprovar recados dos ouvintes">
      <p className="mb-3 text-xs text-slate-500">
        Os recados enviados pelo site chegam como pendentes. Só depois de aprovados aparecem no painel do locutor escolhido.
      </p>
      <div className="mb-4 flex flex-wrap gap-1.5">
        {VIEWS.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setView(item.id)}
            className={`rounded-full px-3 py-1.5 text-xs font-bold ${view === item.id ? 'bg-[#0b2ea8] text-white' : 'bg-slate-100 text-slate-600'}`}
          >
            {item.label} ({counts[item.id]})
          </button>
        ))}
      </div>

      {loading ? <div className="h-20 animate-pulse rounded-xl bg-slate-100" /> : null}
      <div className="space-y-3">
        {current.map((item) => (
          <article key={item._id} className="rounded-xl border border-slate-200 bg-white p-4">
            <p className="mb-1 text-xs font-bold uppercase tracking-wide text-[#0b2ea8]">Para {item.hostName}</p>
            <p className="whitespace-pre-line text-sm leading-relaxed">{item.message}</p>
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-600">
              <span className="font-bold text-slate-800">{item.senderName}</span>
              <a href={`tel:${item.phone}`} className="inline-flex items-center gap-1 text-[#0b2ea8]">
                <Phone size={12} /> {item.phone}
              </a>
              <span className="inline-flex items-center gap-1">
                <MapPin size={12} /> {item.city}
              </span>
              <span>{formatDate(item.createdAt)}</span>
            </div>
            <div className="mt-3 flex flex-wrap gap-2 text-xs font-bold">
              {view !== 'approved' ? (
                <button
                  type="button"
                  onClick={() => void setStatus(item._id, 'approved')}
                  className="inline-flex items-center gap-1 rounded-full bg-green-600 px-3 py-1.5 text-white"
                >
                  <Check size={14} /> Aprovar
                </button>
              ) : null}
              {view !== 'rejected' ? (
                <button
                  type="button"
                  onClick={() => void setStatus(item._id, 'rejected')}
                  className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-3 py-1.5 text-slate-700"
                >
                  <X size={14} /> Rejeitar
                </button>
              ) : null}
              {view !== 'pending' ? (
                <button
                  type="button"
                  onClick={() => void setStatus(item._id, 'pending')}
                  className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1.5 text-slate-600"
                >
                  <Clock size={14} /> Voltar a pendente
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => void remove(item._id)}
                className="inline-flex items-center gap-1 px-2 py-1.5 text-red-500"
              >
                <Trash2 size={14} /> Apagar
              </button>
            </div>
          </article>
        ))}
        {!loading && !current.length ? <p className="text-sm text-slate-500">Nada por aqui.</p> : null}
      </div>
    </Panel>
  )
}

export default MessagesApproval
